import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, LogOut, RefreshCw, Trash2, Calendar as CalIcon } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "untouched", label: "Untouched" },
  { value: "finance", label: "Finance" },
  { value: "collab", label: "Collab" },
  { value: "ecommerce", label: "Ecommerce" },
  { value: "other", label: "Other" },
];

const STAGE_OPTIONS = [
  { value: "unread", label: "Unread", color: "bg-amber-100 text-amber-900 border-amber-300" },
  { value: "open", label: "Open", color: "bg-sky-100 text-sky-900 border-sky-300" },
  { value: "in_progress", label: "In Progress", color: "bg-violet-100 text-violet-900 border-violet-300" },
  { value: "completed", label: "Completed", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  { value: "junk", label: "Junk", color: "bg-stone-200 text-stone-700 border-stone-300" },
];

const TYPE_OPTIONS = [
  { value: "discovery", label: "Discovery Call" },
  { value: "collaboration", label: "Collaboration" },
];

function fmtDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function stageMeta(stage) {
  return STAGE_OPTIONS.find((s) => s.value === stage) || STAGE_OPTIONS[0];
}

function categoryLabel(c) {
  return CATEGORY_OPTIONS.find((x) => x.value === c)?.label || c;
}

function typeLabel(t) {
  return t === "discovery" ? "Discovery" : "Collab";
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ type: "all", category: "all", stage: "all", q: "" });
  const [selected, setSelected] = useState(null); // currently-edited lead

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.type !== "all") params.type = filters.type;
      if (filters.category !== "all") params.category = filters.category;
      if (filters.stage !== "all") params.stage = filters.stage;
      if (filters.q.trim()) params.q = filters.q.trim();
      const [{ data: items }, { data: s }] = await Promise.all([
        api.get("/admin/leads", { params }),
        api.get("/admin/stats"),
      ]);
      setLeads(items);
      setStats(s);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const onUpdate = async (patch) => {
    if (!selected) return;
    try {
      const { data } = await api.patch(
        `/admin/leads/${selected.lead_type}/${selected.id}`,
        patch
      );
      setLeads((prev) => prev.map((l) => (l.id === data.id && l.lead_type === data.lead_type ? data : l)));
      setSelected(data);
      toast.success("Lead updated");
      // Refresh stats too if stage/category changed
      if ("stage" in patch || "category" in patch) {
        const { data: s } = await api.get("/admin/stats");
        setStats(s);
      }
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leads/${lead.lead_type}/${lead.id}`);
      setLeads((prev) => prev.filter((l) => !(l.id === lead.id && l.lead_type === lead.lead_type)));
      setSelected(null);
      toast.success("Lead deleted");
      const { data: s } = await api.get("/admin/stats");
      setStats(s);
    } catch {
      toast.error("Delete failed");
    }
  };

  const unreadCount = stats?.by_stage?.unread || 0;

  const todayFollowUps = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const endOfDay = startOfDay + 24 * 60 * 60 * 1000;
    return leads
      .filter((l) => {
        if (!l.follow_up_at) return false;
        const t = new Date(l.follow_up_at).getTime();
        return !Number.isNaN(t) && t >= startOfDay && t < endOfDay;
      })
      .sort((a, b) => new Date(a.follow_up_at) - new Date(b.follow_up_at));
  }, [leads]);

  const overdueFollowUps = useMemo(() => {
    const n = new Date();
    const startOfToday = new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime();
    return leads
      .filter((l) => {
        if (!l.follow_up_at) return false;
        if (l.stage === "completed" || l.stage === "junk") return false;
        const t = new Date(l.follow_up_at).getTime();
        return !Number.isNaN(t) && t < startOfToday;
      })
      .sort((a, b) => new Date(a.follow_up_at) - new Date(b.follow_up_at));
  }, [leads]);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* HEADER */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-stone-50/85 border-b border-stone-200" data-testid="dashboard-header">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-display text-lg tracking-tight text-ink" data-testid="dash-logo">
              CA&nbsp;Nitin&nbsp;Sharma<span className="text-terracotta">.</span>
            </Link>
            <span className="hidden sm:block text-xs uppercase tracking-[0.18em] text-ink-soft">Lead dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-xs text-ink-soft">{user?.email}</span>
            <button
              onClick={load}
              data-testid="dash-refresh"
              className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-3.5 py-2 text-xs font-medium hover:border-ink"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={logout}
              data-testid="dash-logout"
              className="inline-flex items-center gap-2 rounded-full bg-ink text-white px-4 py-2 text-xs font-medium hover:bg-terracotta transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 md:py-12">
        {/* HERO */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="overline"><span className="section-mark">Pipeline</span></p>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl font-light tracking-[-0.03em] text-ink leading-[1.05]">
              {stats?.total ?? "—"} <span className="font-display-italic">leads</span> total.
            </h1>
            {unreadCount > 0 && (
              <p className="mt-3 text-sm text-ink-soft">
                <span className="inline-flex items-center gap-1.5 mr-1">
                  <span className="w-2 h-2 rounded-full bg-terracotta" />
                </span>
                <span className="text-ink font-medium">{unreadCount}</span> need triage.
              </p>
            )}
          </div>
        </div>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-10">
          {[
            { label: "Total", value: stats?.total ?? 0, key: "total" },
            { label: "Unread", value: stats?.by_stage?.unread ?? 0, key: "unread", color: "text-amber-700" },
            { label: "Open", value: stats?.by_stage?.open ?? 0, key: "open", color: "text-sky-700" },
            { label: "In progress", value: stats?.by_stage?.in_progress ?? 0, key: "ip", color: "text-violet-700" },
            { label: "Completed", value: stats?.by_stage?.completed ?? 0, key: "done", color: "text-emerald-700" },
          ].map((s) => (
            <div
              key={s.key}
              data-testid={`stat-${s.key}`}
              className="rounded-2xl bg-white border border-stone-200 p-5"
            >
              <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-ink-soft">{s.label}</p>
              <p className={`font-display tabular mt-2 text-3xl font-light tracking-tight ${s.color || "text-ink"}`}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* TODAY'S FOLLOW-UPS */}
        {(todayFollowUps.length > 0 || overdueFollowUps.length > 0) && (
          <section className="mb-10" data-testid="follow-ups-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <CalIcon size={16} className="text-terracotta" />
                <p className="overline">Today's follow-ups</p>
              </div>
              <p className="text-xs text-ink-soft">
                <span className="text-ink font-medium">{todayFollowUps.length}</span> due today
                {overdueFollowUps.length > 0 && (
                  <> · <span className="text-red-600 font-medium">{overdueFollowUps.length}</span> overdue</>
                )}
              </p>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
              {overdueFollowUps.map((l) => (
                <FollowUpCard key={`o-${l.lead_type}-${l.id}`} lead={l} overdue onClick={() => setSelected(l)} />
              ))}
              {todayFollowUps.map((l) => (
                <FollowUpCard key={`t-${l.lead_type}-${l.id}`} lead={l} onClick={() => setSelected(l)} />
              ))}
            </div>
          </section>
        )}

        {/* FILTERS */}
        <div className="rounded-2xl bg-white border border-stone-200 p-4 md:p-5 mb-6" data-testid="filters">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
            <div className="md:col-span-4 relative">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
              <input
                data-testid="filter-search"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                placeholder="Search name, email, phone, notes…"
                className="w-full rounded-full border border-stone-200 bg-stone-50 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <FilterSelect
              testid="filter-type"
              label="Type"
              value={filters.type}
              onChange={(v) => setFilters({ ...filters, type: v })}
              options={[{ value: "all", label: "All types" }, ...TYPE_OPTIONS]}
            />
            <FilterSelect
              testid="filter-category"
              label="Category"
              value={filters.category}
              onChange={(v) => setFilters({ ...filters, category: v })}
              options={[{ value: "all", label: "All categories" }, ...CATEGORY_OPTIONS]}
            />
            <FilterSelect
              testid="filter-stage"
              label="Stage"
              value={filters.stage}
              onChange={(v) => setFilters({ ...filters, stage: v })}
              options={[{ value: "all", label: "All stages" }, ...STAGE_OPTIONS]}
            />
            <button
              onClick={() => setFilters({ type: "all", category: "all", stage: "all", q: "" })}
              data-testid="filter-clear"
              className="md:col-span-1 text-xs text-ink-soft hover:text-ink"
            >
              Clear
            </button>
          </div>
        </div>

        {/* LEADS TABLE */}
        <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden" data-testid="leads-table">
          {loading ? (
            <div className="p-12 text-center text-ink-soft text-sm">Loading…</div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center">
              <p className="font-display text-2xl font-light text-ink">No leads match these filters.</p>
              <p className="mt-2 text-sm text-ink-soft">Try clearing them to see everything.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-ink-soft border-b border-stone-200">
                      <th className="px-5 py-3 font-semibold">Name</th>
                      <th className="px-3 py-3 font-semibold">Type</th>
                      <th className="px-3 py-3 font-semibold">Contact</th>
                      <th className="px-3 py-3 font-semibold">Category</th>
                      <th className="px-3 py-3 font-semibold">Stage</th>
                      <th className="px-3 py-3 font-semibold">Follow-up</th>
                      <th className="px-3 py-3 font-semibold">Received</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((l) => {
                      const sm = stageMeta(l.stage);
                      return (
                        <tr
                          key={`${l.lead_type}-${l.id}`}
                          data-testid={`row-${l.id}`}
                          onClick={() => setSelected(l)}
                          className={`border-b border-stone-100 hover:bg-stone-50 cursor-pointer transition-colors ${
                            l.stage === "unread" ? "bg-amber-50/40" : ""
                          }`}
                        >
                          <td className="px-5 py-4">
                            <div className="font-medium text-ink">{l.name}</div>
                            <div className="text-xs text-ink-soft">{l.email}</div>
                          </td>
                          <td className="px-3 py-4">
                            <span className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-medium tracking-wide text-ink-soft uppercase">
                              {typeLabel(l.lead_type)}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-ink tabular">{l.contact}</td>
                          <td className="px-3 py-4 text-ink-soft capitalize">{categoryLabel(l.category)}</td>
                          <td className="px-3 py-4">
                            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide ${sm.color}`}>
                              {sm.label}
                            </span>
                          </td>
                          <td className="px-3 py-4 text-xs text-ink-soft tabular">{fmtDate(l.follow_up_at)}</td>
                          <td className="px-3 py-4 text-xs text-ink-soft tabular">{fmtDate(l.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-stone-100">
                {leads.map((l) => {
                  const sm = stageMeta(l.stage);
                  return (
                    <button
                      key={`${l.lead_type}-${l.id}`}
                      data-testid={`row-m-${l.id}`}
                      onClick={() => setSelected(l)}
                      className={`w-full text-left p-5 hover:bg-stone-50 active:bg-stone-100 ${
                        l.stage === "unread" ? "bg-amber-50/40" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-ink">{l.name}</p>
                          <p className="text-xs text-ink-soft">{l.email}</p>
                          <p className="text-xs text-ink-soft tabular mt-0.5">{l.contact}</p>
                        </div>
                        <span className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${sm.color}`}>
                          {sm.label}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-wider">
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-ink-soft">{typeLabel(l.lead_type)}</span>
                        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-ink-soft">{categoryLabel(l.category)}</span>
                        {l.follow_up_at && (
                          <span className="rounded-full bg-terracotta/10 text-terracotta px-2 py-0.5">
                            ↺ {fmtDate(l.follow_up_at)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>

      <LeadEditor lead={selected} onClose={() => setSelected(null)} onUpdate={onUpdate} onDelete={onDelete} />
    </div>
  );
}

function FilterSelect({ testid, label, value, onChange, options }) {
  return (
    <div className="md:col-span-2">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testid}
          className="w-full rounded-full border border-stone-200 bg-stone-50 px-4 py-2.5 h-auto text-sm focus:ring-0 focus:border-ink"
        >
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`${testid}-${o.value}`}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FollowUpCard({ lead, overdue = false, onClick }) {
  const d = new Date(lead.follow_up_at);
  const time = Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const day = Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  const sm = stageMeta(lead.stage);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`follow-up-${overdue ? "overdue" : "today"}-${lead.id}`}
      className={`snap-start shrink-0 w-[280px] text-left rounded-2xl border bg-white p-4 hover:border-ink transition-colors ${
        overdue ? "border-red-300" : "border-stone-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] tracking-wider text-ink-soft">
          {overdue ? <span className="text-red-600">OVERDUE · {day}</span> : `TODAY · ${time}`}
        </span>
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${sm.color}`}>
          {sm.label}
        </span>
      </div>
      <p className="font-display mt-3 text-xl font-light tracking-tight text-ink leading-snug truncate">
        {lead.name}
      </p>
      <p className="mt-1 text-xs text-ink-soft truncate">{lead.email}</p>
      <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-wider">
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-ink-soft">{typeLabel(lead.lead_type)}</span>
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-ink-soft">{categoryLabel(lead.category)}</span>
      </div>
    </button>
  );
}

function LeadEditor({ lead, onClose, onUpdate, onDelete }) {
  const open = !!lead;
  const [notes, setNotes] = useState("");
  const [followAt, setFollowAt] = useState("");

  useEffect(() => {
    if (lead) {
      setNotes(lead.notes || "");
      setFollowAt(lead.follow_up_at ? toLocalInput(lead.follow_up_at) : "");
    }
  }, [lead]);

  const sm = lead ? stageMeta(lead.stage) : null;

  const saveNotes = async () => {
    await onUpdate({ notes });
  };

  const saveFollowUp = async () => {
    const iso = followAt ? new Date(followAt).toISOString() : null;
    await onUpdate({ follow_up_at: iso });
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto p-0"
        data-testid="lead-editor"
      >
        {lead && (
          <>
            <SheetHeader className="px-6 sm:px-8 pt-7 pb-5 border-b border-stone-200 text-left">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center rounded-full bg-stone-100 px-2.5 py-1 text-[10px] font-medium tracking-wider text-ink-soft uppercase">
                  {typeLabel(lead.lead_type)}
                </span>
                {sm && (
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wide ${sm.color}`}>
                    {sm.label}
                  </span>
                )}
              </div>
              <SheetTitle className="font-display text-3xl font-light tracking-[-0.02em] text-ink leading-tight">
                {lead.name}
              </SheetTitle>
              <SheetDescription className="text-ink-soft text-sm">
                Received {fmtDate(lead.created_at)}
              </SheetDescription>
            </SheetHeader>

            <div className="px-6 sm:px-8 py-7 space-y-8">
              {/* Contact block */}
              <div className="space-y-2 text-sm">
                <Row label="Email">
                  <a href={`mailto:${lead.email}`} className="text-ink hover:text-terracotta">{lead.email}</a>
                </Row>
                <Row label="Phone / WhatsApp">
                  <a href={`https://wa.me/${(lead.contact || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-ink hover:text-terracotta tabular">{lead.contact}</a>
                </Row>
                {lead.city && <Row label="City">{lead.city}</Row>}
                {lead.brand_name && <Row label="Brand">{lead.brand_name}</Row>}
                {lead.timeline && <Row label="Timeline">{lead.timeline.replace(/_/g, " ")}</Row>}
              </div>

              <div>
                <p className="overline mb-3">Requirements</p>
                <p className="text-ink text-[15px] leading-relaxed whitespace-pre-line">{lead.requirements}</p>
              </div>

              {/* Category */}
              <div>
                <p className="overline mb-3">Category</p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      data-testid={`set-category-${c.value}`}
                      onClick={() => onUpdate({ category: c.value })}
                      className={`rounded-full border px-4 py-2 text-xs font-medium tracking-wide transition-colors ${
                        lead.category === c.value
                          ? "bg-ink text-white border-ink"
                          : "border-stone-300 text-ink hover:border-ink"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stage */}
              <div>
                <p className="overline mb-3">Stage</p>
                <div className="flex flex-wrap gap-2">
                  {STAGE_OPTIONS.map((s) => (
                    <button
                      key={s.value}
                      data-testid={`set-stage-${s.value}`}
                      onClick={() => onUpdate({ stage: s.value })}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-wide transition-colors ${
                        lead.stage === s.value ? s.color : "border-stone-300 text-ink-soft hover:text-ink hover:border-ink bg-white"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Follow-up */}
              <div>
                <p className="overline mb-3">Next follow-up</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <CalIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                    <input
                      data-testid="follow-up-input"
                      type="datetime-local"
                      value={followAt}
                      onChange={(e) => setFollowAt(e.target.value)}
                      className="w-full rounded-full border border-stone-300 pl-10 pr-4 py-2.5 text-sm text-ink focus:outline-none focus:border-ink"
                    />
                  </div>
                  <button
                    onClick={saveFollowUp}
                    data-testid="follow-up-save"
                    className="rounded-full bg-ink text-white px-5 py-2.5 text-xs font-medium tracking-wide hover:bg-terracotta transition-colors"
                  >
                    Save
                  </button>
                  {lead.follow_up_at && (
                    <button
                      onClick={() => { setFollowAt(""); onUpdate({ follow_up_at: null }); }}
                      data-testid="follow-up-clear"
                      className="rounded-full border border-stone-300 px-5 py-2.5 text-xs font-medium tracking-wide hover:border-ink"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <p className="overline mb-3">Notes</p>
                <textarea
                  data-testid="notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  placeholder="Internal notes about this lead…"
                  className="w-full rounded-2xl border border-stone-300 p-4 text-sm text-ink resize-none focus:outline-none focus:border-ink leading-relaxed"
                />
                <button
                  onClick={saveNotes}
                  data-testid="notes-save"
                  className="mt-3 rounded-full bg-ink text-white px-5 py-2.5 text-xs font-medium tracking-wide hover:bg-terracotta transition-colors"
                >
                  Save notes
                </button>
              </div>

              {/* Danger zone */}
              <div className="pt-6 border-t border-stone-200">
                <button
                  onClick={() => onDelete(lead)}
                  data-testid="delete-lead"
                  className="inline-flex items-center gap-2 text-xs font-medium text-red-600 hover:text-red-800"
                >
                  <Trash2 size={14} /> Delete lead
                </button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-stone-100 last:border-0">
      <p className="w-32 shrink-0 text-[11px] uppercase tracking-[0.18em] text-ink-soft pt-0.5">{label}</p>
      <div className="flex-1 text-ink">{children}</div>
    </div>
  );
}

function toLocalInput(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
